import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBullhorn, 
  faGraduationCap, 
  faCalendarAlt, 
  faMoneyBillWave,
  faUserGraduate,
  faBook,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
  faArrowRight,
  faSpinner,
  faTimes,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import '../styles/InformasiSection.css';

const InformasiSection = ({ kategori = null, limit = 6, title = "Pusat Informasi", subtitle = "Informasi terbaru seputar pendaftaran dan akademik" }) => {
  const [informasi, setInformasi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);

  // Map icon names to FontAwesome icons
  const iconMap = {
    'school': faGraduationCap,
    'checklist': faCheckCircle,
    'event': faCalendarAlt,
    'money': faMoneyBillWave,
    'user': faUserGraduate,
    'book': faBook,
    'info': faInfoCircle,
    'warning': faExclamationTriangle,
    'announcement': faBullhorn,
    'default': faInfoCircle
  };

  // Map kategori to icons
  const kategoriIcon = {
    'pengumuman': faBullhorn,
    'berita': faInfoCircle,
    'event': faCalendarAlt,
    'akademik': faBook,
    'keuangan': faMoneyBillWave,
    'pmb': faGraduationCap,
    'alumni': faUserGraduate,
    'umum': faInfoCircle
  };

  // Map prioritas to colors
  const prioritasColor = {
    'urgent': '#ef4444',
    'tinggi': '#f59e0b',
    'normal': '#3b82f6',
    'rendah': '#6b7280'
  };

  // Determine API base URL
  const getApiUrl = () => {
    // Development mode (React dev server on port 3000)
    if (window.location.port === '3000') {
      return 'http://localhost/stikbar_webv2/public';
    }
    // Production mode
    if (window.location.hostname === 'stikesbaramuli.ac.id' || window.location.hostname === 'www.stikesbaramuli.ac.id') {
      return 'https://stikesbaramuli.ac.id';
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl = getApiUrl();
        let url = `${baseUrl}/api/informasi.php?limit=${limit}&target=public`;
        if (kategori) {
          url += `&kategori=${kategori}`;
        }
        
        const response = await fetch(url);
        const result = await response.json();
        
        if (result.success) {
          setInformasi(result.data);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Gagal memuat informasi');
        console.error('Error fetching informasi:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [kategori, limit]);

  const getIcon = (iconName, kategoriName) => {
    if (iconName && iconMap[iconName]) {
      return iconMap[iconName];
    }
    if (kategoriName && kategoriIcon[kategoriName]) {
      return kategoriIcon[kategoriName];
    }
    return iconMap['default'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const openModal = (info) => {
    setSelectedInfo(info);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedInfo(null);
    document.body.style.overflow = 'auto';
  };

  if (loading) {
    return (
      <section className="informasi-section section">
        <div className="container">
          <div className="informasi-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Memuat informasi...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="informasi-section section">
        <div className="container">
          <div className="informasi-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
          </div>
        </div>
      </section>
    );
  }

  if (informasi.length === 0) {
    return null; // Don't render section if no data
  }

  return (
    <section className="informasi-section section">
      <div className="container">
        <div className="section-title">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>

        <div className="informasi-grid">
          {informasi.map((info) => (
            <div 
              key={info.id} 
              className={`informasi-card ${info.is_pinned ? 'pinned' : ''} prioritas-${info.prioritas}`}
              onClick={() => openModal(info)}
            >
              {info.is_pinned === 1 && (
                <div className="pinned-badge">
                  <FontAwesomeIcon icon={faBullhorn} /> Penting
                </div>
              )}
              
              <div 
                className="informasi-icon" 
                style={{ background: info.warna || prioritasColor[info.prioritas] || '#3b82f6' }}
              >
                <FontAwesomeIcon icon={getIcon(info.icon, info.kategori)} />
              </div>

              <div className="informasi-content">
                <span className="informasi-kategori">{info.kategori}</span>
                <h4>{info.judul}</h4>
                <p>{truncateText(info.ringkasan || info.konten, 80)}</p>
                <div className="informasi-meta">
                  <span className="informasi-date">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {formatDate(info.tanggal_mulai)}
                  </span>
                  <span className="informasi-read">
                    Baca <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Detail */}
      {selectedInfo && (
        <div className="informasi-modal-overlay" onClick={closeModal}>
          <div className="informasi-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <FontAwesomeIcon icon={faTimes} />
            </button>

            <div className="modal-header">
              <div 
                className="modal-icon" 
                style={{ background: selectedInfo.warna || '#3b82f6' }}
              >
                <FontAwesomeIcon icon={getIcon(selectedInfo.icon, selectedInfo.kategori)} />
              </div>
              <div className="modal-title">
                <span className="modal-kategori">{selectedInfo.kategori}</span>
                <h3>{selectedInfo.judul}</h3>
                <span className="modal-date">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  {formatDate(selectedInfo.tanggal_mulai)}
                </span>
              </div>
            </div>

            <div className="modal-body">
              {selectedInfo.gambar && (
                <img src={selectedInfo.gambar} alt={selectedInfo.judul} className="modal-image" />
              )}
              <div className="modal-content" dangerouslySetInnerHTML={{ __html: selectedInfo.konten.replace(/\n/g, '<br/>') }} />
            </div>

            {selectedInfo.link_url && (
              <div className="modal-footer">
                <a 
                  href={selectedInfo.link_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Selengkapnya <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default InformasiSection;
